import { LoadingSpinner } from "components/LoadingSpinner";
import { parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IContractModelingRow,
  useGetContractModelingAvailableCptCodesQuery,
  useGetContractModelingPayersWithDataQuery,
  usePostContractModelingAnalysisMutation,
} from "store/queries/contractModeling";
import { useGetAllPayersQuery } from "store/queries/payers";
import styled from "styled-components";

import { exportToCSV } from "../MarketPriceAnalysis/helpers";

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;

  .p-multiselect .p-multiselect-label {
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .p-datatable {
    .bg-primary-100 {
      background-color: #f0f6ff !important;

      td {
        font-weight: 600;
        border-top: 2px solid var(--primary-color);
      }
    }
  }
`;

function formatIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDollars(value: number) {
  return parsePrice(value);
}

function getMutationErrorMessage(err: unknown): string {
  if (!err || typeof err !== "object") return "Analysis request failed.";
  const anyErr = err as {
    data?: { message?: string };
    message?: string;
  };
  return anyErr.data?.message || anyErr.message || "Analysis request failed.";
}

export function ContractModelingAnalysis() {
  const [primaryPayerId, setPrimaryPayerId] = useState<number | null>(null);
  const [comparisonPayerIds, setComparisonPayerIds] = useState<number[]>([]);
  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);
  const [serviceDateRange, setServiceDateRange] = useState<Date[]>([]);
  const [first, setFirst] = useState(0);
  const [rows] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);

  const startDate = useMemo(
    () => formatIsoDate(serviceDateRange[0] ?? null),
    [serviceDateRange]
  );
  const endDate = useMemo(
    () => formatIsoDate(serviceDateRange[1] ?? null),
    [serviceDateRange]
  );

  const { data: payersData, isLoading: isLoadingPayersCatalog } =
    useGetAllPayersQuery();
  const {
    data: payersWithModelingData = [],
    isLoading: isLoadingPayersWithData,
  } = useGetContractModelingPayersWithDataQuery();
  const { data: cptOptions = [], isLoading: isLoadingCpts } =
    useGetContractModelingAvailableCptCodesQuery({
      payerId: primaryPayerId ?? undefined,
      startDate,
      endDate,
    });

  const [
    postContractModelingAnalysis,
    {
      data: analysisData,
      isLoading: isLoadingAnalysis,
      error: analysisError,
      reset: resetAnalysisFn,
    },
  ] = usePostContractModelingAnalysisMutation();

  const resetAnalysisRef = useRef(resetAnalysisFn);
  resetAnalysisRef.current = resetAnalysisFn;
  const resetAnalysis = useCallback(() => resetAnalysisRef.current(), []);

  const payerOptions = useMemo(() => {
    if (!payersWithModelingData.length) return [];

    const catalogById = new Map<number, { name: string; archivedAt?: string }>(
      (payersData?.data ?? []).map(
        (payer: { id: number; name: string; archivedAt?: string }) => [
          payer.id,
          { name: payer.name, archivedAt: payer.archivedAt },
        ]
      )
    );

    return payersWithModelingData
      .map((ref) => {
        const catalog = catalogById.get(ref.id);
        const label = (
          ref.name?.trim() ||
          catalog?.name ||
          `Payer ${ref.id}`
        ).trim();
        if (catalog?.archivedAt) return null;
        if (label === "Clear Health") return null;
        return { label, value: ref.id };
      })
      .filter((o): o is { label: string; value: number } => o !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [payersWithModelingData, payersData]);

  const comparisonOptions = useMemo(() => {
    return payerOptions.filter(
      (o: { label: string; value: number }) => o.value !== primaryPayerId
    );
  }, [payerOptions, primaryPayerId]);

  const distinctComparisonIds = useMemo(() => {
    if (primaryPayerId == null) return [];
    return [...new Set(comparisonPayerIds)].filter(
      (id) => id !== primaryPayerId
    );
  }, [comparisonPayerIds, primaryPayerId]);

  const MAX_INDIVIDUAL_COLUMNS = 3;
  const isAggregatedMode =
    distinctComparisonIds.length > MAX_INDIVIDUAL_COLUMNS;

  const comparisonColumnOrder = useMemo(() => {
    if (!analysisData?.rows?.length) return [];
    return analysisData.rows[0].comparisonPayers.map((c) => c.payerId);
  }, [analysisData]);

  useEffect(() => {
    setComparisonPayerIds((prev) => {
      const filtered = prev.filter((id) => id !== primaryPayerId);
      return filtered.length === prev.length ? prev : filtered;
    });
    setFirst(0);
    resetAnalysis();
  }, [primaryPayerId, resetAnalysis]);

  const comparisonKey = comparisonPayerIds.join(",");
  const cptKey = selectedCptCodes.join(",");

  useEffect(() => {
    setFirst(0);
  }, [comparisonKey, cptKey, startDate, endDate]);

  useEffect(() => {
    if (distinctComparisonIds.length === 0) {
      resetAnalysis();
    }
  }, [distinctComparisonIds.length, resetAnalysis]);

  useEffect(() => {
    if (!primaryPayerId || distinctComparisonIds.length === 0) {
      return;
    }

    postContractModelingAnalysis({
      primaryPayerId,
      comparisonPayerIds,
      cptCodes: selectedCptCodes.length > 0 ? selectedCptCodes : undefined,
      startDate,
      endDate,
    });
  }, [primaryPayerId, comparisonKey, cptKey, startDate, endDate]);

  const tableData = useMemo(() => {
    if (!analysisData?.rows?.length) return [];

    const paginatedRows = analysisData.rows.slice(first, first + rows);

    const compCount = analysisData.totals.comparisonPayerRevenues.length || 1;
    const avgTotalModeledRevenue =
      analysisData.totals.comparisonPayerRevenues.reduce(
        (s, t) => s + t.totalModeledRevenue,
        0
      ) / compCount;

    const totalsRow = {
      cptCode: "Totals",
      volume: 0,
      primaryPayer: {
        avgRate: 0,
        revenue: analysisData.totals.primaryPayerRevenue,
      },
      comparisonPayers: analysisData.totals.comparisonPayerRevenues.map(
        (t) => ({
          payerId: t.payerId,
          payerName: t.payerName,
          avgRate: 0,
          modeledRevenue: t.totalModeledRevenue,
          rateChangePercent: 0,
        })
      ),
      revenuePotential: analysisData.totals.revenuePotential.difference,
      isTotalsRow: true,
      comparisonAvgRevenue: avgTotalModeledRevenue,
    };

    return [...paginatedRows, totalsRow] as Array<
      IContractModelingRow & {
        isTotalsRow?: boolean;
        comparisonAvgRevenue?: number;
      }
    >;
  }, [analysisData, first, rows]);

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
  };

  const handleDownloadData = async () => {
    if (!analysisData?.rows.length) return;
    try {
      setIsDownloading(true);
      const csvRows = analysisData.rows.map((row: IContractModelingRow) => {
        const base: Record<string, string | number> = {
          CPT: row.cptCode,
          Volume: row.volume,
          "Primary revenue": formatDollars(row.primaryPayer.revenue),
        };
        if (isAggregatedMode) {
          const count = row.comparisonPayers.length || 1;
          const avg =
            row.comparisonPayers.reduce((s, c) => s + c.modeledRevenue, 0) /
            count;
          base["Comparison avg revenue"] = formatDollars(avg);
        } else {
          row.comparisonPayers.forEach((cell) => {
            base[`${cell.payerName} revenue`] = formatDollars(
              cell.modeledRevenue
            );
          });
        }
        base["Revenue potential"] = formatDollars(row.revenuePotential);
        return base;
      });
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      exportToCSV(csvRows, `contract_modeling_${timestamp}.csv`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoadingPayersCatalog || isLoadingPayersWithData) {
    return <LoadingSpinner />;
  }

  if (!payersWithModelingData.length) {
    return (
      <StyledContainer className="bg-white">
        <div className="p-4">
          <div className="flex gap-2 align-items-center mb-3">
            <i className="pi pi-table text-2xl text-primary" />
            <p className="text-lg font-bold">Contract modeling</p>
          </div>
          <div className="text-center p-4 text-gray-500">
            No EDI claims with line items were found for this account, so
            contract modeling is not available yet.
          </div>
        </div>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer className="bg-white">
      <div className="p-4">
        <div className="flex justify-content-between align-items-center mb-3">
          <div className="flex gap-2 align-items-center">
            <i className="pi pi-table text-2xl text-primary" />
            <p className="text-lg font-bold">Contract modeling</p>
          </div>
          {analysisData &&
            !isLoadingAnalysis &&
            analysisData.rows.length > 0 && (
              <Button
                label="Download"
                icon="pi pi-download"
                onClick={handleDownloadData}
                loading={isDownloading}
                className="p-button-outlined max-h-3rem max-w-12rem"
              />
            )}
        </div>

        <div className="flex flex-wrap gap-3 mb-4 align-items-end">
          <div className="flex flex-column gap-2" style={{ minWidth: "220px" }}>
            <label className="text-sm font-semibold">Primary payer</label>
            <Dropdown
              value={primaryPayerId}
              options={payerOptions}
              onChange={(e) => setPrimaryPayerId(e.value ?? null)}
              placeholder="Select primary payer"
              showClear
              filter
            />
          </div>

          <div
            className="flex flex-column gap-2 flex-grow-1"
            style={{ minWidth: "260px" }}
          >
            <label className="text-sm font-semibold">Comparison payers</label>
            <MultiSelect
              value={comparisonPayerIds}
              options={comparisonOptions}
              onChange={(e) => setComparisonPayerIds(e.value ?? [])}
              placeholder={
                !primaryPayerId
                  ? "Select a primary payer first"
                  : "Select one or more payers to compare"
              }
              filter
              showClear
              display="chip"
              className="w-full"
              disabled={!primaryPayerId}
            />
          </div>

          <div className="flex flex-wrap gap-3 align-items-end">
            <div
              className="flex flex-column gap-2"
              style={{ minWidth: "240px", width: "240px", maxWidth: "100%" }}
            >
              <label className="text-sm font-semibold">
                Service date range
              </label>
              <Calendar
                selectionMode="range"
                placeholder="Service Date Range"
                value={serviceDateRange}
                onChange={(e: { value: CalendarValueType }) => {
                  const dates = e.value as Date[];
                  setServiceDateRange(dates || []);
                }}
                showIcon
                dateFormat="mm/dd/yy"
                className="w-full"
              />
            </div>

            <div
              className="flex flex-column gap-2"
              style={{ width: "200px", maxWidth: "100%" }}
            >
              <label className="text-sm font-semibold">CPT (optional)</label>
              <MultiSelect
                value={selectedCptCodes}
                options={cptOptions.map((c) => ({ label: c, value: c }))}
                onChange={(e) => setSelectedCptCodes(e.value ?? [])}
                placeholder={isLoadingCpts ? "Loading…" : "All if empty"}
                filter
                showClear
                display="comma"
                maxSelectedLabels={2}
                className="w-full"
                disabled={isLoadingCpts}
                emptyFilterMessage="No CPT codes match"
              />
            </div>
          </div>
        </div>

        {analysisError && (
          <div className="p-3 mb-3 border-round border-1 border-red-200 bg-red-50 text-red-900 text-sm">
            {getMutationErrorMessage(analysisError)}
          </div>
        )}

        {isLoadingAnalysis && (
          <div className="flex justify-content-center p-5">
            <LoadingSpinner />
          </div>
        )}

        {primaryPayerId &&
          distinctComparisonIds.length > 0 &&
          analysisData &&
          !isLoadingAnalysis && (
            <div className="w-full">
              {analysisData.rows.length > 0 ? (
                <>
                  <DataTable
                    value={tableData}
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                    className="mb-4"
                    rowClassName={(rowData) => ({
                      "bg-primary-100 font-bold": rowData.isTotalsRow,
                    })}
                  >
                    <Column
                      field="cptCode"
                      header="CPT Code"
                      style={{ width: "100px" }}
                    />
                    <Column
                      field="volume"
                      header="Volume"
                      body={(
                        row: IContractModelingRow & {
                          isTotalsRow?: boolean;
                        }
                      ) => (row.isTotalsRow ? "-" : row.volume)}
                      style={{ width: "80px" }}
                    />
                    <Column
                      header="Primary Revenue"
                      body={(row: IContractModelingRow) =>
                        formatDollars(row.primaryPayer.revenue)
                      }
                      style={{ width: "130px" }}
                    />
                    {isAggregatedMode ? (
                      <Column
                        header="Comparison avg"
                        body={(
                          row: IContractModelingRow & {
                            isTotalsRow?: boolean;
                            comparisonAvgRevenue?: number;
                          }
                        ) => {
                          if (row.isTotalsRow) {
                            return formatDollars(row.comparisonAvgRevenue ?? 0);
                          }
                          const count = row.comparisonPayers.length || 1;
                          const avg =
                            row.comparisonPayers.reduce(
                              (s, c) => s + c.modeledRevenue,
                              0
                            ) / count;
                          return formatDollars(avg);
                        }}
                        style={{ width: "140px" }}
                      />
                    ) : (
                      comparisonColumnOrder.map((payerId) => {
                        const sampleName =
                          analysisData.rows[0]?.comparisonPayers.find(
                            (c) => c.payerId === payerId
                          )?.payerName ?? `Payer ${payerId}`;
                        return (
                          <Column
                            key={payerId}
                            header={sampleName}
                            body={(row: IContractModelingRow) => {
                              const cell = row.comparisonPayers.find(
                                (c) => c.payerId === payerId
                              );
                              return cell
                                ? formatDollars(cell.modeledRevenue)
                                : "—";
                            }}
                            style={{ width: "140px" }}
                          />
                        );
                      })
                    )}
                    <Column
                      header="Revenue Potential"
                      body={(row: IContractModelingRow) =>
                        formatDollars(row.revenuePotential)
                      }
                      style={{ width: "140px" }}
                    />
                  </DataTable>
                  {analysisData.rows.length > rows && (
                    <Paginator
                      first={first}
                      rows={rows}
                      totalRecords={analysisData.rows.length}
                      onPageChange={onPageChange}
                    />
                  )}
                </>
              ) : (
                <div className="text-center p-4 text-gray-500">
                  No CPT rows matched the selected payers and filters.
                </div>
              )}
            </div>
          )}

        {!primaryPayerId && !isLoadingAnalysis && (
          <div className="text-center p-4 text-gray-500">
            Select a primary payer to run contract modeling.
          </div>
        )}

        {primaryPayerId &&
          distinctComparisonIds.length === 0 &&
          !isLoadingAnalysis && (
            <div className="text-center p-4 text-gray-500">
              Select at least one comparison payer other than the primary payer.
            </div>
          )}
      </div>
    </StyledContainer>
  );
}
